import { ldb, state } from '@codesuma/baseline'
import { RATES_ENDPOINT } from '../configs/app'

// Market prices — currencies, coins and gold, and crypto — each with its
// change since yesterday, for the ticker over the calendar and the page it
// opens. They come from the prices API in api/, which also decides which
// prices there are and what they are called.

export type RateGroup = 'currency' | 'gold' | 'crypto'
// Prices are in tomans, and the ounce and most crypto in dollars.
export type RateUnit = 'toman' | 'dollar'

export interface IRate {
    id: string
    name: string
    group: RateGroup
    unit: RateUnit
    price: number
    change: number // since yesterday, in the price's own unit
    percent: number // that change as a share of yesterday's price
}

// Every price, and when the source last updated them.
export interface IRates {
    rates: IRate[]
    updated: number
}

export const UNIT_NAMES: Record<RateUnit, string> = {
    'toman': 'تومان',
    'dollar': 'دلار',
}

// The prices saved from the last answer, at once: the ticker and the page draw
// these first, while load() asks for fresh ones, and still have them offline.
// Nothing at all before the first answer.
const SAVED = 'rates'
const saved = (): IRates => ldb.get(SAVED) || { rates: [], updated: 0 }

// The latest prices, saved as they come. Raw fetch for the reason in
// services/hijri-adjustments: the API answers any origin with *, which a
// request carrying credentials cannot accept.
async function load(): Promise<IRates> {
    try {
        const res = await fetch(RATES_ENDPOINT, { cache: 'no-cache' })
        const found = res.ok ? await res.json() as IRates : null
        if (Array.isArray(found?.rates) && Number.isFinite(found.updated)) {
            ldb.set(SAVED, found)
            return found
        }
    } catch {
        // Offline, or the API is down: the saved prices stand.
    }
    return saved()
}

// What the ticker shows: whether it runs at all — the switch at the top of the
// prices page, on until it is turned off — and which prices, by id, the ones
// most people look up first until the reader picks their own. Turning it off
// keeps the picks for when it comes back. Either change is broadcast as
// 'rates-ticker'.
const ON_KEY = 'rates-on'
const KEY = 'rates-shown'
let on = ldb.get(ON_KEY) !== false
let shown: string[] = ldb.get(KEY) || ['usd', 'eur', 'coin-new', 'gold-18', 'usdt']

const isOn = () => on
const isShown = (id: string) => shown.includes(id)

function turn(value: boolean) {
    on = value
    ldb.set(ON_KEY, on)
    state.set('rates-ticker', { on, shown })
}

function show(id: string, value: boolean) {
    shown = value ? [...shown, id] : shown.filter(s => s !== id)
    ldb.set(KEY, shown)
    state.set('rates-ticker', { on, shown })
}

// Persian digits and the Persian thousands mark, with only as many decimals as
// a price that small needs: none from a thousand up, up to four below it. The
// figures are unsigned: which way a price went is the arrow's to say.
const whole = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 })
const fine = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 4 })
const share = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 2 })

export const formatAmount = (n: number) => (Math.abs(n) >= 1000 ? whole : fine).format(Math.abs(n))
export const formatPercent = (n: number) => `${share.format(Math.abs(n))}٪`
export const arrow = (r: IRate) => r.change > 0 ? '▲' : '▼'

// How long ago, as ۵ دقیقه پیش, ۳ ساعت پیش or دیروز. Not a clock time: the
// reader may be in another time zone from the source.
const ago = new Intl.RelativeTimeFormat('fa-IR', { numeric: 'auto' })
export const formatAgo = (ts: number) => {
    const minutes = Math.floor((Date.now() - ts) / 60000)
    if (minutes < 1) return 'همین الان'
    if (minutes < 60) return ago.format(-minutes, 'minute')
    if (minutes < 60 * 24) return ago.format(-Math.floor(minutes / 60), 'hour')
    return ago.format(-Math.floor(minutes / (60 * 24)), 'day')
}

// The ticker's round figure, the way a price is said: from a million up in
// millions — سکه ۲۵۹ for 259,080,000 — from a thousand up in thousands —
// یورو ۳۰۲ for 302,210 — whole below that, and two figures under ten, where a
// whole number would say nothing.
const brief = new Intl.NumberFormat('fa-IR', { maximumSignificantDigits: 2 })
export const formatRound = (n: number) =>
    n >= 1e6 ? whole.format(n / 1e6)
        : n >= 1000 ? whole.format(n / 1000)
            : (n >= 10 ? whole : brief).format(n)

export default { saved, load, isOn, isShown, turn, show }
